/**
 * Rerolling a Check with AI Favor. Pure, so it is tested directly.
 *
 * RULE DCC-FAVOR-002 (C&C p.16): spend 1 AI Favor to reroll the d20, once per
 * Check, before the GM announces the result; a natural 1 cannot be rerolled.
 *
 * TABLE RULING (owner, 2026-09-16), because the book does not say which die an
 * Advantage or Disadvantage Check rerolls: the whole roll is rerolled in the
 * same mode (two dice, keep the higher or the lower again), and the new roll
 * replaces the old one, better or worse. Modifiers are those the Check was made
 * with; nothing else about the Check (Mana, marks, Actions) happens again.
 */
import type { Character } from '@dcc/rules/domain/character.ts';
import type { SessionState } from '@dcc/rules/domain/session.ts';
import type { Roller } from '@dcc/rules/rules/dice.ts';
import type { CheckResult } from '@dcc/rules/rules/checks.ts';
import type { CheckHistoryEntry, RollHistoryEntry } from '@dcc/rules/rules/rollHistory.ts';
import { isDamageEntry } from '@dcc/rules/rules/rollHistory.ts';
import { rollD20 } from '@dcc/rules/rules/rollModes.ts';
import { spendAiFavor } from '@dcc/rules/rules/aiFavor.ts';
import { applyChanges } from '@dcc/rules/rules/characterChanges.ts';
import { notationLabel } from './diceRolls.ts';

/** Appended to a rerolled Check's label; also how "once per Check" is enforced. */
export const REROLL_MARK = ' (AI Favor reroll)';

const SOURCE = 'play' as const;

export function wasRerolled(entry: RollHistoryEntry): boolean {
  return entry.label.endsWith(REROLL_MARK);
}

/** Why this entry cannot be rerolled now, or null when it can. */
export function rerollProblem(character: Character, entry: RollHistoryEntry): string | null {
  if (isDamageEntry(entry)) return 'Only d20 Checks can be rerolled with AI Favor.';
  if (wasRerolled(entry)) return 'This Check has already been rerolled.';
  const spend = spendAiFavor(character.aiFavor, 'reroll-d20', { dieFace: entry.keptDie });
  return spend.ok ? null : spend.reason;
}

/** The modifiers the Check was made with, recovered from what it recorded. */
function modifierOf(entry: CheckHistoryEntry): number {
  return entry.total - entry.keptDie;
}

/** Dice+ notation for the reroll: the same dice and modifier as the original Check. */
export function rerollNotation(entry: CheckHistoryEntry): string {
  const count = entry.dice.length;
  const keep = count === 1 ? '' : entry.mode === 'advantage' ? 'kh1' : 'kl1';
  const modifier = modifierOf(entry);
  const mod = modifier === 0 ? '' : modifier > 0 ? `+${modifier}` : `-${Math.abs(modifier)}`;
  return `${count}d20${keep}${mod} #${notationLabel(`${entry.label} AI Favor reroll`)}`;
}

export type RerollOutcome =
  | { ok: true; character: Character; session: SessionState; entry: CheckHistoryEntry }
  | { ok: false; reason: string };

export function rerollWithAiFavor(
  character: Character,
  session: SessionState,
  entryId: string,
  roller: Roller,
  now: string,
): RerollOutcome {
  const original = session.rollHistory.find((e) => e.id === entryId);
  if (!original) return { ok: false, reason: 'That roll is no longer in the history.' };
  const problem = rerollProblem(character, original);
  if (problem !== null || isDamageEntry(original)) return { ok: false, reason: problem ?? 'Only d20 Checks can be rerolled.' };

  const spent = spendAiFavor(character.aiFavor, 'reroll-d20', { dieFace: original.keptDie });
  if (!spent.ok) return { ok: false, reason: spent.reason };
  const changed = applyChanges(
    character,
    [{ change: { kind: 'set-ai-favor', aiFavor: spent.remaining }, source: SOURCE, reason: 'AI Favor reroll (C&C p.16)' }],
    { now },
  );
  if (!changed.ok) return { ok: false, reason: changed.problems.map((p) => p.message).join(' ') };

  const die = rollD20(roller, original.mode);
  const entry: CheckHistoryEntry = {
    ...original,
    dice: die.dice,
    keptDie: die.kept,
    total: modifierOf(original) + die.kept,
    label: `${original.label}${REROLL_MARK}`,
    timestamp: now,
  };
  return {
    ok: true,
    character: changed.character,
    session: { ...session, rollHistory: session.rollHistory.map((e) => (e.id === entryId ? entry : e)) },
    entry,
  };
}

/** The Attack Check as the damage rules should now see it, after a reroll. */
export function checkAfterReroll(check: CheckResult, entry: CheckHistoryEntry): CheckResult {
  return {
    ...check,
    die: { ...check.die, dice: [...entry.dice], kept: entry.keptDie },
    total: entry.total,
    naturalTwenty: entry.keptDie === 20,
    naturalOne: entry.keptDie === 1,
  };
}
